import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CameraListener, nativeService } from '../../service';
import { UseCameraProps } from './useCamera.types';

/**
 * Opens the SDK camera over a mini that stays mounted behind it, and receives
 * the result.
 *
 * Several components may use this at once — a capture screen has one per photo
 * slot — and only the one that launched the camera is given the result.
 *
 * There is no completion signal for a camera the user backs out of, and none
 * for a denied permission either: the SDK closes its overlay and tells the
 * webview nothing. So `takePhoto` resolves nothing and returns nothing. Keep
 * the control that triggered it enabled, show progress only once a result has
 * actually arrived, and a dismissal costs the user one wasted tap instead of a
 * screen stuck on a spinner.
 *
 * @example
 * const { isSupported, takePhoto } = useCamera({ onPhoto: setReceipt });
 *
 * return isSupported
 *   ? <Button label="Take photo" onClick={takePhoto} />
 *   : <FileUpload onFilesSelected={([file]) => setReceiptFile(file)} />;
 */
export const useCamera = ({ onPhoto, onCode }: UseCameraProps = {}) => {
  const callbacksRef = useRef<UseCameraProps>({ onPhoto, onCode });

  callbacksRef.current = { onPhoto, onCode };

  // One stable listener for the lifetime of the hook: the service keys the
  // active request on this object's identity, so re-creating it when a
  // callback prop changes would orphan a capture already in flight.
  const listenerRef = useRef<CameraListener>({
    onPhoto: (base64Image) => callbacksRef.current.onPhoto?.(base64Image),
    onCode: (code) => callbacksRef.current.onCode?.(code),
  });

  useEffect(() => nativeService.subscribeToCamera(listenerRef.current), []);

  const takePhoto = useCallback(
    () => nativeService.launchCamera('take-photo', listenerRef.current),
    [],
  );

  const scanCode = useCallback(
    () => nativeService.launchCamera('scan-code', listenerRef.current),
    [],
  );

  /**
   * Opens the photo library rather than the camera. The result arrives through
   * the same `onPhoto`, since the SDK runs both through one encoder.
   *
   * Guard it with `isPhotoLibrarySupported`, not `isSupported`: a build can
   * have the camera without the library — any SDK from 7.2.75 up to 7.2.77.
   */
  const pickFromLibrary = useCallback(
    () => nativeService.launchCamera('open-photo-library', listenerRef.current),
    [],
  );

  // Fixed for the session: the SDK cannot gain the bridge while the webview is
  // open, and re-reading it per render would only invite it into deps arrays.
  const isSupported = useMemo(() => nativeService.isCameraSupported(), []);

  const isPhotoLibrarySupported = useMemo(
    () => nativeService.isPhotoLibrarySupported(),
    [],
  );

  return {
    isSupported,
    isPhotoLibrarySupported,
    takePhoto,
    scanCode,
    pickFromLibrary,
  };
};

export default useCamera;
